<template>
    <header>
        <div v-show="isOpen"
             class="fixed left-0 top-0 right-0 bottom-0 z-40"/>
        <div class="fixed top-0 left-0 right-0 transition-transform duration-500 transform z-50 p-5
                    lg:flex lg:items-center lg:px-10 lg:py-0"
             :class="[ pelicanRouge ? 'lg:bg-cream' : 'lg:bg-white', {'lg:-translate-y-full': isSticky && !scrollUp} ]">
            <div class="flex justify-between items-center w-full border rounded-xl py-4 px-3
                        lg:bg-none lg:rounded-none lg:border-none lg:py-0 lg:p-0"
                 :class="[mobileOpen ? 'border-transparent' : 'border-light-gray', pelicanRouge ? 'bg-cream' : 'bg-white' ]">
                <div class="lg:py-4">
                    <slot name="logo"/>
                </div>
                <div class="hidden justify-end items-center gap-8 py-4 lg:flex">
                    <slot name="top-nav"/>
                    <slot name="site"/>
                    <slot name="lang"/>
                    <slot name="cta"/>
                </div>
                <div class="lg:hidden flex gap-6 items-center">
                    <slot name="mobile-cta" :mobileOpen="mobileOpen"/>
                    <div class="cursor-pointer " @click="toggleMobile">
                        <icon v-if="!mobileOpen" name='burger' class='w-6 h-6 fill-current'/>
                        <icon v-else name='close' class='w-6 h-6 fill-current'/>
                    </div>
                </div>
            </div>
        </div>
        <div class="fixed top-0 left-0 right-0 h-dvh transition-transform duration-500 transform pointer-events-none z-40
                    lg:top-15.5 lg:flex lg:items-center lg:h-auto lg:px-10"
             :class="{'hidden': !mobileOpen, 'lg:-translate-y-10': isSticky && !scrollUp}">
            <div class="relative h-full bg-light-gray overflow-auto pointer-events-auto px-8 py-36 pb-28 mx-auto
                        lg:self-end lg:h-auto lg:border  lg:rounded-xl lg:overflow-visible lg:pt-3 lg:px-8 lg:pb-2 transition-all"
                 :class="[{'notch': !isSticky}, isSticky && !isOpen && !scrollUp ? 'lg:border-light-gray' : 'lg:border-transparent', pelicanRouge ? 'lg:bg-cream' : 'lg:bg-white']"
                 ref="mainNav">
                <slot name="main" :isOpen="isOpen" :open="open" :close="close" :isSticky="isSticky" :scrollUp="scrollUp"/>
                <collapse-transition>
                    <div v-if="isOpen"
                         class="fixed left-0 top-0 right-0 h-dvh rounded-2xl lg:h-auto lg:-z-1 lg:mx-10 lg:mt-3"
                         @click.stop>
                        <slot name="flyout" :open="open" :close="close" :currentItem="currentItem" :index="this.openedIndex" :sticky="isSticky"/>
                    </div>
                </collapse-transition>
                <div class="mt-8 lg:hidden">
                    <slot name="meta-mobile"/>
                </div>
                <div class="mt-8 lg:hidden">
                    <slot name="lang"/>
                </div>
            </div>
        </div>
        <div v-show="mobileOpen"
             class="fixed bottom-0 left-0 w-full transition-all duration-500 z-50 px-5 pb-5 lg:hidden"
             :class="{'top-26': mobileSiteOpen}">
            <div class="flex flex-col justify-between gap-8 h-full rounded-xl px-3 p-2" :class="pelicanRouge ? 'bg-cream' : 'bg-white'">
                <slot name="mobile-site" :mobileSiteOpen="mobileSiteOpen" :toggle="toggleMobileSite" :toggleMobile="toggleMobile"/>
            </div>
        </div>
    </header>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Provide, ProvideReactive, Ref } from 'vue-property-decorator';
import Icon from '../base/Icon.vue';
import NavigationItem from './NavigationItem.vue';
import { CollapseTransition } from '@ivanv/vue-collapse-transition';

@Component({
    components: {
        Icon, CollapseTransition
    }
})
export default class SelectaHeader extends Vue {
    @Prop({ default: false, type: Boolean }) hasCta: boolean;
    @Prop({ default: false, type: Boolean }) pelicanRouge?: boolean;
    @Ref() mainNav: HTMLDivElement;

    items: NavigationItem[] = [];
    scrollPos = -1;
    scrollUp = false;
    mobileOpen = false;
    mobileSiteOpen = false;
    openedIndex = -1;

    mounted(): void {
        document.addEventListener('scroll', this.onScroll);
        document.addEventListener('click', this.onOutsideClick);
    }

    get isSticky(): boolean {
        return this.scrollPos > 0;
    }

    get isOpen(): boolean {
        return this.openedIndex > -1;
    }

    get currentItem(): NavigationItem {
        return this.items[this.openedIndex];
    }

    @Provide()
    private register(item: NavigationItem): void {
        this.items.push(item);
        item.$on('click', this.onItemClicked);
    }

    close(): void {
        this.openedIndex = -1;
        this.checkState();
    }

    open(index: number): void {
        if (this.items[index]) {
            this.onItemClicked(this.items[index], true);
        }
    }

    toggleMobile(): void {
        this.mobileOpen = !this.mobileOpen;
        this.checkState();
    }

    toggleMobileSite(): void {
        this.mobileSiteOpen = !this.mobileSiteOpen;
    }

    onItemClicked(item: NavigationItem, keepOpenOnSame?: boolean) {
        const index = this.items.findIndex(x => x._uid === item._uid);
        if (index >= 0) {
            this.openedIndex = (index === this.openedIndex && !keepOpenOnSame) ? -1 : index;
        }
        this.checkState();
    }

    onOutsideClick(e: Event) {
        if (!this.mainNav?.contains(e.target as Node)) {
            this.openedIndex = -1;
            this.checkState();
        }
    }

    checkState(): void {
        if ((this.openedIndex > -1) || this.mobileOpen) {
            document.body.classList.add('overflow-hidden');
        } else {
            document.body.classList.remove('overflow-hidden');
        }
    }

    onScroll(): void {
        const lastScrollPos = this.scrollPos;
        this.scrollPos = document.scrollingElement?.scrollTop || 0;
        this.scrollUp = this.scrollPos < lastScrollPos;
    }
}
</script>
